import React, { useState, useContext } from 'react';
import { useForm } from '../hooks/useForm';
import { useSnackbar } from '../contexts/SnackbarContext'; // Import useSnackbar
import { UserContext } from '../contexts/UserContext';
import {
    TextField,
    Button,
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    CardActions,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Tooltip,
    Paper,
    Checkbox, Link,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import Grid from '@mui/material/Grid2'; // Import Grid from Grid2
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { httpRequest } from '../services/httpService';
import ConfirmationDialog from '../components/ConfirmationDialog'; // Import the new component
import Loader from '../components/Loader';

export default function ClinicForm() {
    const fetchUrl = '/api/clinic/current'; // Fetch user clinic data
    const saveUrl = '/api/clinic/current';

    const validate = (data) => {
        const errors = {};
        if (!data.title) errors.title = 'Title is required';
        return errors;
    };

    const {
        data,
        isLoading,
        error,
        validationErrors,
        handleInputChange,
        handleSubmit,
    } = useForm({ fetchUrl, saveUrl, validate });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentEmployee, setCurrentEmployee] = useState({});
    const showSnackbar = useSnackbar(); // Use the useSnackbar hook
    const { user } = useContext(UserContext); // Get user from context
    const [confirmationDialog, setConfirmationDialog] = useState({
        open: false,
        title: '',
        message: '',
        confirmButtonText: '',
        confirmButtonColor: '',
        onConfirm: null,
    });

    const handleDialogClose = () => {
        setDialogOpen(false);
        setCurrentEmployee({});
    };

    const handleDialogSave = async () => {
        try {
            await httpRequest(
                '/api/clinic/employee',
                { method: 'POST', body: currentEmployee },
                showSnackbar, // Use showSnackbar directly
                'Guardado!'
            );

            // Fetch updated clinic data
            const updatedClinic = await httpRequest('/api/clinic/current', { method: 'GET' });
            handleInputChange({ target: { name: 'employees', value: updatedClinic.employees } });

            handleDialogClose();
        } catch (error) {
            // Error handling is already managed by httpRequest
        }
    };

    const handleAddEmployee = () => {
        setCurrentEmployee({ fullName: '', email: '', phone: '', isManager: false });
        setDialogOpen(true);
    };

    const handleEditEmployee = (employee) => {
        setCurrentEmployee(employee);
        setDialogOpen(true);
    };

    const handleBlockEmployee = (employee) => {
        setConfirmationDialog({
            open: true,
            title: 'Confirmar Bloqueo',
            message: '¿Confirmar bloqueo? El empleado no podrá utilizar el sistema.',
            confirmButtonText: 'Bloquear',
            confirmButtonColor: 'warning',
            onConfirm: () => toggleEmployeeLock(employee),
        });
    };

    const handleUnblockEmployee = (employee) => {
        setConfirmationDialog({
            open: true,
            title: 'Confirmar Acceso',
            message: '¿Confirmar acceso de usuario?',
            confirmButtonText: 'Permitir',
            confirmButtonColor: 'success',
            onConfirm: () => toggleEmployeeLock(employee),
        });
    };

    const handleConfirmationDialogClose = () => {
        setConfirmationDialog({ ...confirmationDialog, open: false });
    };

    const toggleEmployeeLock = async (employee) => {
        try {
            await httpRequest(
                `/api/clinic/${employee.isLocked ? 'unblock' : 'block'}`,
                { method: 'POST', body: employee.id },
                showSnackbar, // Use showSnackbar directly
                employee.isLocked ? 'Empleado desbloqueado con éxito!' : 'Empleado bloqueado con éxito!'
            );

            // Fetch updated clinic data
            const updatedClinic = await httpRequest('/api/clinic/current', { method: 'GET' });
            handleInputChange({ target: { name: 'employees', value: updatedClinic.employees } });
        } catch (error) {
            // Error handling is already managed by httpRequest
        } finally {
            handleConfirmationDialogClose();
        }
    };

    // Helper to check if user has Admin or Manager role
    const canEdit = user && user.roles && (user.roles.includes('Admin') || user.roles.includes('Manager'));

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <>
            <Box component="form" onSubmit={handleSubmit} sx={{ mx: 'auto' }}>
                {/* Header */}
                <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                >
                    <Typography variant="h5">Editar Clínica</Typography>
                </Box>
                <Card sx={{ padding: 2 }}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    label="Título"
                                    name="title"
                                    value={data.title}
                                    onChange={handleInputChange}
                                    error={!!validationErrors.title}
                                    helperText={validationErrors.title}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    label="Teléfono"
                                    name="phone"
                                    value={data.phone}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    label="Dirección"
                                    name="address"
                                    value={data.address}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    label="URL del sitio web"
                                    name="webUrl"
                                    value={data.webUrl}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Última actualización: {new Date(data.updated).toLocaleString()}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                    <CardActions>
                        <Button type="submit" variant="contained" color="primary" disabled={!canEdit}>
                            Guardar
                        </Button>
                    </CardActions>
                </Card>

                {/* Employees Section */}
                <Box sx={{ mt: 4 }}>
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                        <Typography variant="h6">Veterinarios</Typography>
                        <IconButton color="primary" onClick={handleAddEmployee} disabled={!canEdit}>
                            <AddIcon />
                        </IconButton>
                    </Box>
                    <TableContainer component={Paper}>
                        <Table size="small" padding="dense">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell>Correo</TableCell>
                                    <TableCell>Teléfono</TableCell>
                                    <TableCell>Manager</TableCell>
                                    <TableCell>Modificado</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.employees.map((employee) => (
                                    <TableRow key={employee.id} hover>
                                        <TableCell>{employee.fullName}</TableCell>
                                        <TableCell>
                                            <Link href={`mailto:${employee.email}`}
                                                  onClick={(e) => e.stopPropagation()}>{employee.email}</Link>
                                        </TableCell>
                                        <TableCell>{employee.phone}</TableCell>
                                        <TableCell>
                                            <Checkbox checked={employee.isManager} disabled sx={{padding: 0}}/>
                                        </TableCell>
                                        <TableCell>
                                            {new Date(employee.updated).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Editar" arrow>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleEditEmployee(employee)}
                                                    disabled={!canEdit}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                title={employee.isLocked ? 'Desbloquear' : 'Bloquear'}
                                                arrow
                                            >
                                                <IconButton
                                                    size="small"
                                                    color={employee.isLocked ? 'warning' : 'success'}
                                                    onClick={() =>
                                                        employee.isLocked
                                                            ? handleUnblockEmployee(employee)
                                                            : handleBlockEmployee(employee)
                                                    }
                                                    disabled={!canEdit}
                                                >
                                                    {employee.isLocked ? <LockIcon /> : <LockOpenIcon />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>

            {/* Employee Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>{currentEmployee.id ? 'Editar Veterinario' : 'Agregar Veterinario'}</DialogTitle>
                <DialogContent>
                    <TextField
                        variant={'standard'}
                        fullWidth
                        margin="dense"
                        label="Nombre Completo"
                        name="fullName"
                        value={currentEmployee.fullName || ''}
                        onChange={(e) => setCurrentEmployee({ ...currentEmployee, fullName: e.target.value })}
                    />
                    <TextField
                        variant={'standard'}
                        fullWidth
                        margin="dense"
                        label="Correo"
                        name="email"
                        value={currentEmployee.email || ''}
                        onChange={(e) => setCurrentEmployee({ ...currentEmployee, email: e.target.value })}
                    />
                    <TextField
                        variant={'standard'}
                        fullWidth
                        margin="dense"
                        label="Teléfono"
                        name="phone"
                        value={currentEmployee.phone || ''}
                        onChange={(e) => setCurrentEmployee({ ...currentEmployee, phone: e.target.value })}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Checkbox
                            checked={currentEmployee.isManager || false}
                            onChange={(e) => setCurrentEmployee({ ...currentEmployee, isManager: e.target.checked })}
                        />
                        <Typography>Manager</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary">Cancelar</Button>
                    <Button onClick={handleDialogSave} color="primary" variant="contained">Guardar</Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmationDialog.open}
                title={confirmationDialog.title}
                message={confirmationDialog.message}
                confirmButtonText={confirmationDialog.confirmButtonText}
                confirmButtonColor={confirmationDialog.confirmButtonColor}
                onClose={handleConfirmationDialogClose}
                onConfirm={confirmationDialog.onConfirm}
            />
        </>
    );
}

